import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Dialog,
  DialogContent,
  IconButton,
  Chip,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import {
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import Seo from '../components/Seo';
import api, { IMAGE_BASE_URL } from '../services/api';

// Lazy loading image component with compression
const LazyImage = ({ src, alt, sx, onLoad }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  const handleLoad = () => {
    setLoaded(true);
    if (onLoad) onLoad();
  };

  const handleError = () => {
    setError(true);
    setLoaded(true);
  };

  return (
    <>
      {!loaded && !error && (
        <Skeleton
          variant="rectangular"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
          }}
        />
      )}
      <CardMedia
        component="img"
        image={src}
        alt={alt}
        loading="lazy"
        onLoad={handleLoad}
        onError={handleError}
        sx={{
          ...sx,
          opacity: loaded ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out',
        }}
      />
    </>
  );
};

const GalleryDynamic = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [galleryCategories, setGalleryCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    fetchGalleryData();
    
    // Ensure scroll is enabled on mount
    document.body.style.overflow = 'auto';
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  const fetchGalleryData = async () => {
    try {
      setLoading(true);
      console.log('[GalleryDynamic] Fetching all gallery items...');
      
      // Fetch all gallery items
      const response = await api.get('/GalleryItem');
      const allItems = (response.data || []).filter(
        (item) => item.isActive !== false && item.category?.isActive !== false
      );
      console.log('[GalleryDynamic] Received active items:', allItems.length);

      // Group by category and collect all images
      const categoryMap = new Map();
      
      allItems.forEach(item => {
        const catName = item.category?.name || 'Uncategorized';
        const catId = item.category?.categoryID || 0;
        
        if (!categoryMap.has(catId)) {
          categoryMap.set(catId, {
            categoryID: catId,
            name: catName,
            title: item.category?.title || catName,
            subtitle: item.category?.subtitle || '',
            description: item.category?.description || '',
            images: [],
          });
        }
        
        const cat = categoryMap.get(catId);
        cat.images.push({
          ImagePath: item.imagePath,
          title: item.title,
          description: item.description,
          isMainImage: item.isMainImage,
          displayOrder: item.displayOrder,
        });
      });

      const categories = Array.from(categoryMap.values());
      console.log('[GalleryDynamic] Processed categories:', categories.length);
      categories.forEach(cat => {
        console.log(`[GalleryDynamic] ${cat.name}: Images=${cat.images.length}`);
      });
      
      setGalleryCategories(categories);
      setError(null);
    } catch (err) {
      console.error('Error fetching gallery data:', err);
      setError('Failed to load gallery');
      setGalleryCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const openLightbox = async (category) => {
    try {
      console.log('[GalleryDynamic] Fetching images for category:', category.categoryID);
      
      // Fetch all images for this category
      const response = await api.get('/GalleryItem');
      const allItems = (response.data || []).filter(
        (item) => item.isActive !== false && item.category?.isActive !== false
      );
      
      // Filter by category
      const categoryImages = allItems
        .filter(item => item.category?.categoryID === category.categoryID)
        .sort((a, b) => a.displayOrder - b.displayOrder);
      
      console.log('[GalleryDynamic] Category images:', categoryImages.length);
      
      setCurrentCategory({
        ...category,
        allImages: categoryImages
      });
      setCurrentImageIndex(0);
      setLightboxOpen(true);
    } catch (err) {
      console.error('Error fetching category images:', err);
    }
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setCurrentCategory(null);
  };

  const nextImage = () => {
    if (currentCategory && currentCategory.allImages) {
      setCurrentImageIndex((prev) => (prev + 1) % currentCategory.allImages.length);
    }
  };

  const prevImage = () => {
    if (currentCategory && currentCategory.allImages) {
      setCurrentImageIndex((prev) => 
        (prev - 1 + currentCategory.allImages.length) % currentCategory.allImages.length
      );
    }
  };

useEffect(() => {
  const handleKeyPress = (e) => {
    if (e.key === "Escape") closeLightbox();
    if (e.key === "ArrowRight") nextImage();
    if (e.key === "ArrowLeft") prevImage();
  };

  document.addEventListener("keydown", handleKeyPress);

  return () => {
    document.removeEventListener("keydown", handleKeyPress);
  };
}, [lightboxOpen]);

  // Get unique category names for filters
  const categoryNames = ['All Photos', ...new Set(galleryCategories.map(cat => cat.name))];
  
  // Flatten all images from filtered categories
  const filteredCategories = selectedCategory === 'all' 
    ? galleryCategories 
    : galleryCategories.filter(cat => cat.name === selectedCategory);
  
  const allImages = filteredCategories.flatMap(category => 
    category.images.map(img => ({
      ...img,
      categoryName: category.name,
      categoryTitle: category.title,
    }))
  );

  const lightboxImages = currentCategory?.allImages || [];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
      <Seo
        title="Sintra Hotel Islamabad Pictures | Sintra Hotel Gallery "
        description="Hotel gallery, Islamabad Hotel Booking, Book hotel in Islamabad, hotels in Islamabad booking, hotel room Islamabad, luxury hotel in Islamabad, Islamabad hotels, top hotel in Islamabad, "
        keywords="Luxury Hotel gallery, hotel photos, rooms images, suites photos, facilities, restaurant photos, Pakistan hotel"
        canonical="https://thesintrahotel.com/sintra-hotel-islamabad-pictures"

      />
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: '360px', md: '430px' },
          display: 'flex',
          alignItems: 'flex-end',
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.28), rgba(0,0,0,0.38)), url(/img/home/hotel.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, pb: { xs: 6, md: 9 } }}>
          <Box sx={{ maxWidth: '520px', color: '#fff', textAlign: 'left' }}>
            <Typography
              sx={{
                fontSize: '12px',
                letterSpacing: '6px',
                textTransform: 'uppercase',
                mb: 1.5,
                color: '#ffffff',
              }}
            >
              Images & Videos
            </Typography>
            <Typography
              component="h1"
              sx={{
                fontFamily: 'Gilda Display, serif',
                fontWeight: 400,
                fontSize: { xs: '42px', md: '60px' },
                lineHeight: 1.15,
                color: '#ffffff',
              }}
            >
              Our Gallery
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ pt: { xs: 7, md: 10 }, pb: 2 }}>
        <Box sx={{ width: '100%' }}>
          <Typography
            component="h2"
            sx={{
              fontFamily: 'Gilda Display, serif',
              fontWeight: 400,
              fontSize: { xs: '34px', md: '54px' },
              color: '#0f172a',
              lineHeight: 1.18,
              mb: 3,
              width: '100%',
            }}
          >
            Explore Sintra Hotel Islamabad Pictures Gallery
          </Typography>
          <p>
            Welcome to the Sintra Hotel Islamabad Pictures Gallery—your visual journey into luxury, comfort, and style. Situated in the heart of Islamabad&apos;s vibrant Melody area, near the bustling Blue Area, Sintra Hotel offers a unique blend of elegance and convenience, making it the perfect choice for family travellers, tourists, business professionals, and individuals alike. Our hotel gallery provides a first-hand look at the premium amenities and sophisticated atmosphere that define our hotel. From beautifully designed luxury hotel rooms pictures to serene outdoor spaces and fine dining experiences, every image reflects the warmth and quality of the Sintra stay experience.
          </p>
        </Box>
      </Container>

      <Container maxWidth="xl" sx={{ py: 8 }}>
        {/* Category Filter */}
        <Box sx={{ mb: 6 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
            <Chip
              label="All Photos"
              onClick={() => setSelectedCategory('all')}
              variant={selectedCategory === 'all' ? 'filled' : 'outlined'}
              color={selectedCategory === 'all' ? 'primary' : 'default'}
              sx={{
                px: 2,
                py: 1,
                fontSize: '0.9rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 2,
                },
              }}
            />
            {[...new Set(galleryCategories.map(cat => cat.name))].map((categoryName) => (
                <Chip
                  key={categoryName}
                  label={categoryName}
                  onClick={() => setSelectedCategory(categoryName)}
                  variant={selectedCategory === categoryName ? 'filled' : 'outlined'}
                  color={selectedCategory === categoryName ? 'primary' : 'default'}
                  sx={{
                    px: 2,
                    py: 1,
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 2,
                    },
                  }}
                />
            ))}
          </Box>
        </Box>

        {/* Gallery Grid - All Images */}
        {/* Gallery Grid */}
<div className="row gx-4 gy-4">
  {allImages.map((image, index) => (
    <div
      className="col-lg-4 col-md-6 gallery-item"
      key={`${image.categoryName}-${index}`}
    >
      <a
        href="#!"
        onClick={(e) => {
          e.preventDefault();

          setCurrentCategory({
            name: selectedCategory === 'all' ? 'All Photos' : image.categoryName,
            allImages,
          });
          setCurrentImageIndex(index);
          setLightboxOpen(true);
        }}
        className="img-zoom"
      >
        <div className="gallery-box position-relative overflow-hidden">
          <div className="gallery-img">
            <img
              src={`${IMAGE_BASE_URL}${
                image.ImagePath || "/uploads/gallery/default.jpg"
              }`}
              className="img-fluid w-100"
              alt={image.title}
              style={{
                height: "320px",
                objectFit: "cover",
                borderRadius: "8px",
                transition: "0.4s",
                cursor: "pointer",
              }}
            />
          </div>
        </div>
      </a>
    </div>
  ))}
</div>



        {allImages.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary">
              No gallery items found.
            </Typography>
          </Box>
        )}
      </Container>

      {/* Lightbox/Slider */}
<Dialog
  open={lightboxOpen}
  onClose={closeLightbox}
  maxWidth={false}
  fullScreen
  disableScrollLock
  PaperProps={{
    sx: {
      background: "rgba(0,0,0,0.92)",
      overflow: "hidden",
    },
  }}
>
  <DialogContent
    sx={{
      p: 0,
      position: "relative",
      width: "100%",
      height: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    {/* Close Button */}
    <IconButton
      onClick={closeLightbox}
      sx={{
        position: "absolute",
        top: 20,
        right: 20,
        color: "#fff",
        zIndex: 20,
        background: "rgba(255,255,255,0.15)",
        "&:hover": {
          background: "rgba(255,255,255,0.25)",
        },
      }}
    >
      <CloseIcon sx={{ fontSize: 32 }} />
    </IconButton>

    {/* Left Arrow */}
    {lightboxImages.length > 1 && (
      <IconButton
        onClick={prevImage}
        sx={{
          position: "absolute",
          left: 25,
          top: "50%",
          transform: "translateY(-50%)",
          color: "#fff",
          zIndex: 20,
          background: "rgba(255,255,255,0.15)",
          width: 60,
          height: 60,
          "&:hover": {
            background: "rgba(255,255,255,0.25)",
          },
        }}
      >
        <ChevronLeftIcon sx={{ fontSize: 50 }} />
      </IconButton>
    )}

    {/* Main Image */}
    {lightboxImages[currentImageIndex] && (
      <Box
        component="img"
        src={`${IMAGE_BASE_URL}${lightboxImages[currentImageIndex].ImagePath}`}
        alt={lightboxImages[currentImageIndex].title}
        sx={{
          maxWidth: "80vw",
          maxHeight: "85vh",
          objectFit: "contain",
          borderRadius: "4px",
          boxShadow: "0 0 30px rgba(0,0,0,0.5)",
        }}
      />
    )}

    {/* Right Arrow */}
    {lightboxImages.length > 1 && (
      <IconButton
        onClick={nextImage}
        sx={{
          position: "absolute",
          right: 25,
          top: "50%",
          transform: "translateY(-50%)",
          color: "#fff",
          zIndex: 20,
          background: "rgba(255,255,255,0.15)",
          width: 60,
          height: 60,
          "&:hover": {
            background: "rgba(255,255,255,0.25)",
          },
        }}
      >
        <ChevronRightIcon sx={{ fontSize: 50 }} />
      </IconButton>
    )}

    {/* Bottom Counter */}
    {lightboxImages.length > 0 && (
      <Box
        sx={{
          position: "absolute",
          bottom: 20,
          left: "50%",
          transform: "translateX(-50%)",
          color: "#fff",
          background: "rgba(0,0,0,0.6)",
          px: 3,
          py: 1,
          borderRadius: "30px",
          fontSize: "14px",
          letterSpacing: "1px",
        }}
      >
        {currentImageIndex + 1} of {lightboxImages.length}
      </Box>
    )}
  </DialogContent>
</Dialog>
    </Box>
  );
};

export default GalleryDynamic;